import { IrregularConsumptionAlert } from './../models/irregular-consumption-alert.interface';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { ApiResponse, ApiResponseV2 } from 'src/app/models/api-response.interface';
import { CommonService } from 'src/app/services/common.service';
import { BillPublishResponse } from '../models/bill-publish-response.interface';
import { AlertResolutionResponse } from '../models/consumption-alerts-resolution.interface';
import { IrregularConsumptionTank } from '../models/irregular-consumption-tank.interface';
import { ConsumptionAlertStatus } from '../models/consumption-alert-status.interface';
import { CommercialCondominium, CommercialReportFilters } from '../../commercial-condominium-report/models/commercial-condominium-response.model';
import { CondominiumHab } from '../../condominium-hab-report/models/condominium-hab.model';
import { HousingTank } from '../../housing-tanks-report/models/housing-tanks.model';
import {
  extractReportColumnSummaries,
  COMMERCIAL_REPORT_SUMMARY_HEADERS,
  CommercialReportResponse,
  GLOBAL_HABITATIONAL_SUMMARY_HEADERS,
  HabitationalReportResponse,
  HOUSING_TANKS_SUMMARY_HEADERS,
  ReportColumnSummaryHeader
} from '../../habitational-report.utils';

@Injectable({
  providedIn: 'root'
})
export class IrregularConsumptionsReportService extends CommonService {
  token: string;
  options;
  constructor(
    private httpClient: HttpClient
  ) {
    super(httpClient);
    this.token = sessionStorage.getItem('token')!;
    this.options = this.getHeaders(this.token);
  }

  getHousingTanksAlerts(payload: Partial<{ page: number, per_page: number, settlement_id: number | string, tanque_id: number | string, building_id: number | string, created_at_since: string, created_at_to: string }>): Observable<HabitationalReportResponse<HousingTank>> {
    return this.getHabitationalReport(
      'residential_tank_reports',
      payload,
      HOUSING_TANKS_SUMMARY_HEADERS
    );
  }

  getCondominiumsHabAlerts(payload: Partial<{ page: number, per_page: number, settlement_id: number | string, unit_id: number | string, bill_id: number, created_at_since: string, created_at_to: string }>): Observable<HabitationalReportResponse<CondominiumHab>> {
    return this.getHabitationalReport(
      'unit_reports',
      payload,
      GLOBAL_HABITATIONAL_SUMMARY_HEADERS
    );
  }

  getCommercialCondominiumsAlerts(payload: CommercialReportFilters & { page?: number; per_page?: number }): Observable<CommercialReportResponse<CommercialCondominium>> {
    const params = Object.entries(payload as Record<string, any>)
    .filter(([_, value]) => value !== '' && value !== null && value !== undefined)
    .reduce((acc, [key, value]) => acc.set(key, value), new HttpParams());
    return this.http.get<ApiResponse<CommercialCondominium>>(`${this.apiURL}/commercial_condominium_reports`, {
      headers: this.options.headers,
      params,
      observe: 'response'
    }).pipe(
      map(response => {
        if (!response.body) {
          throw new Error('Empty response received from commercial_condominium_reports');
        }

        return {
          ...response.body,
          columnSummaries: extractReportColumnSummaries(
            headerName => response.headers.get(headerName),
            COMMERCIAL_REPORT_SUMMARY_HEADERS
          )
        };
      })
    );
  }

  getConsumptionAlerts(payload: Partial<{ page: number, per_page: number, settlement_tipo: number, settlement_id: number | string, consumption_alert_status_id: number | string, created_at_since: string, created_at_to: string }>): Observable<ApiResponse<IrregularConsumptionAlert>> {
    const params = Object.entries(payload as Record<string, any>)
    .filter(([_, value]) => value !== '' && value !== null && value !== undefined)
    .reduce((acc, [key, value]) => acc.set(key, value), new HttpParams());
    return this.http.get<ApiResponse<IrregularConsumptionAlert>>(`${this.apiURL}/consumption_alerts`, {
      headers: this.options.headers,
      params
    });
  }

  getConsumptionAlertStatuses(): Observable<ConsumptionAlertStatus[]> {
    return this.http.get<ConsumptionAlertStatus[]>(`${this.apiURL}/consumption_alert_statuses`, {
      headers: this.options.headers
    });
  }

  getConsumptionAlertsAnova(payload: Partial<{ page: number, per_page: number, created_at_since: string, created_at_to: string }>): Observable<ApiResponseV2<IrregularConsumptionTank>> {
    const params = Object.entries(payload as Record<string, any>)
    .filter(([_, value]) => value !== '' && value !== null && value !== undefined)
    .reduce((acc, [key, value]) => acc.set(key, value), new HttpParams());
    return this.http.get<ApiResponseV2<IrregularConsumptionTank>>(`${this.apiURL}/tank_consumption_alerts`, {
      headers: this.options.headers,
      params
    });
  }

  getHousingTanksHabCsv(payload: Partial<{ settlement_id: number | string, tanque_id: number | string, building_id: number | string, created_at_since: string, created_at_to: string }>): Observable<Blob> {
    const params = Object.entries(payload as Record<string, any>)
    .filter(([_, value]) => value !== '' && value !== null && value !== undefined)
    .reduce((acc, [key, value]) => acc.set(key, value), new HttpParams());
    const headers = new HttpHeaders({
      'Accept': 'text/csv',
      'Authorization': `Bearer ${this.token}`
    });
    
    return this.http.get(`${this.apiURL}/residential_tank_reports`, {
      headers,
      params,
      responseType: 'blob'
    });
  }

  getCondominiumHabCsv(payload: Partial<{ settlement_id: number | string, unit_id: number | string, created_at_since: string, created_at_to: string }>): Observable<Blob> {
    const params = Object.entries(payload as Record<string, any>)
    .filter(([_, value]) => value !== '' && value !== null && value !== undefined)
    .reduce((acc, [key, value]) => acc.set(key, value), new HttpParams());
    const headers = new HttpHeaders({
      'Accept': 'text/csv',
      'Authorization': `Bearer ${this.token}`
    });
    
    return this.http.get(`${this.apiURL}/unit_reports`, {
      headers,
      params,
      responseType: 'blob'
    });
  }

  getCondominiumCommercialCsv(payload: CommercialReportFilters): Observable<Blob> {
    const params = Object.entries(payload as Record<string, any>)
    .filter(([_, value]) => value !== '' && value !== null && value !== undefined)
    .reduce((acc, [key, value]) => acc.set(key, value), new HttpParams());
    const headers = new HttpHeaders({
      'Accept': 'text/csv',
      'Authorization': `Bearer ${this.token}`
    });
    
    return this.http.get(`${this.apiURL}/commercial_condominium_reports`, {
      headers,
      params,
      responseType: 'blob'
    });
  }

  getConsumptionAlertsCsv(payload: Partial<{ settlement_tipo: number, settlement_id: number | string, consumption_alert_status_id: number | string, created_at_since: string, created_at_to: string }>): Observable<Blob> {
    const params = Object.entries(payload as Record<string, any>)
    .filter(([_, value]) => value !== '' && value !== null && value !== undefined)
    .reduce((acc, [key, value]) => acc.set(key, value), new HttpParams());
    const headers = new HttpHeaders({
      'Accept': 'text/csv',
      'Authorization': `Bearer ${this.token}`
    });
    
    return this.http.get(`${this.apiURL}/consumption_alerts`, {
      headers,
      params,
      responseType: 'blob'
    });
  }

  getConsumptionAnovaAlertsCsv(payload: Partial<{ created_at_since: string, created_at_to: string }>): Observable<Blob> {
    const params = Object.entries(payload as Record<string, any>)
    .filter(([_, value]) => value !== '' && value !== null && value !== undefined)
    .reduce((acc, [key, value]) => acc.set(key, value), new HttpParams());
    const headers = new HttpHeaders({
      'Accept': 'text/csv',
      'Authorization': `Bearer ${this.token}`
    });
    
    return this.http.get(`${this.apiURL}/tank_consumption_alerts`, {
      headers,
      params,
      responseType: 'blob'
    });
  }

  updateAlert(id: number, status: number): Observable<ApiResponse<any>> {
    return this.http.patch<ApiResponse<any>>(`${this.apiURL}/consumption_alerts/${id}/consumption_alert_statuses`, {
      consumption_alert_status_id: status
    }, this.options);
  }

  updateTankAlert(id: number, status: number): Observable<ApiResponse<any>> {
    return this.http.patch<ApiResponse<any>>(`${this.apiURL}/tank_consumption_alerts/${id}/tank_consumption_alert_statuses`, {
      consumption_alert_status_id: status
    }, this.options);
  }

  updateTankResolution(id: number, resolutionId: number) {
    return this.http.patch<ApiResponse<any>>(`${this.apiURL}/tank_consumption_alerts/${id}/tank_consumption_alert_resolutions`, {
      consumption_alert_resolution_id: resolutionId
    }, this.options);
  }

  billCanPublish(billId: number): Observable<BillPublishResponse> {
    return this.http.get<BillPublishResponse>(`${this.apiURL}/bills/${billId}?virtual=CAN_PUBLISH`, this.options);
  }

  alertResolutions(forGasMeter: boolean = false): Observable<AlertResolutionResponse> {
    const flag = forGasMeter ? '?is_for_gas_meters=true' : '?is_for_tanks=true';
    return this.http.get<AlertResolutionResponse>(`${this.apiURL}/consumption_alert_resolutions${flag}`);
  }

  patchResolution(billId: number, resolutionId: number) {
    return this.http.patch(`${this.apiURL}/consumption_alerts/${billId}/consumption_alert_resolutions`, { consumption_alert_resolution_id: resolutionId }, this.options);
  }

  private getHabitationalReport<T>(
    endpoint: string,
    payload: Record<string, unknown>,
    summaryHeaders: readonly ReportColumnSummaryHeader[]
  ): Observable<HabitationalReportResponse<T>> {
    const params = Object.entries(payload)
      .filter(([_, value]) => value !== '' && value !== null && value !== undefined)
      .reduce((acc, [key, value]) => acc.set(key, String(value)), new HttpParams());

    return this.http.get<ApiResponse<T>>(`${this.apiURL}/${endpoint}`, {
      headers: this.options.headers,
      params,
      observe: 'response'
    }).pipe(
      map(response => {
        if (!response.body) {
          throw new Error(`Empty response received from ${endpoint}`);
        }

        return {
          ...response.body,
          columnSummaries: extractReportColumnSummaries(
            headerName => response.headers.get(headerName),
            summaryHeaders
          )
        };
      })
    );
  }
}
